// store.js
import { ref, watch } from 'vue';
import axios from 'axios';
import VueCookies from 'vue-cookies';

// Ensures we only fetch once per successful request
let fetched = false;

// Read stored year from cookies/localStorage, fallback to current year
const storedYear = VueCookies.get('selectedYear')
  || localStorage.getItem('selectedYear')
  || new Date().getFullYear();

// Reactive state
export const selectedYear = ref(Number(storedYear));
export const yearOptions = ref([]);

/**
 * Fetches years from API with improved failure handling.
 * - Prevents duplicate calls.
 * - Retries if authentication fails.
 */
export async function fetchYears(retry = false) {
  if (fetched && !retry) return;  // Prevent duplicate calls unless retrying

  const token = localStorage.getItem('access_token');
  if (!token) {
    console.warn("No token found. Will retry fetchYears when authenticated.");
    fetched = false;  // Allow reattempt once a token is available
    return;
  }

  try {
    const response = await axios.get('/api/v1/years/', {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });

    // Convert raw year numbers into { label, value } objects for dropdowns
    yearOptions.value = response.data.years.map(year => ({
      label: String(year),
      value: year
    }));

    // Ensure `selectedYear` is in the list; otherwise default to current year
    if (!yearOptions.value.some(item => item.value === selectedYear.value)) {
      selectedYear.value = new Date().getFullYear();
    }

    fetched = true; // Mark as successfully fetched
  } catch (error) {
    console.error('Failed to fetch years:', error);

    if (error.response && error.response.status === 401) {
      console.warn("Authentication issue detected. Will retry after login.");
      fetched = false;  // Allow retry after login
      setTimeout(() => fetchYears(true), 5000); // Retry after a delay
    } else {
      console.warn("Network error detected. Retrying in 5 seconds...");
      fetched = false;
      setTimeout(() => fetchYears(true), 5000); // Retry after a delay
    }
  }
}

// Attempt fetching only if a token exists
if (localStorage.getItem('access_token')) {
  fetchYears();
}

// Watch for token changes (e.g., login) and retry fetching years
watch(() => localStorage.getItem('access_token'), (newToken) => {
  if (newToken) {
    fetchYears(true);  // Retry fetching years after login
  }
});

// Watch for changes in `selectedYear` and persist them
watch(selectedYear, (newValue) => {
  VueCookies.set('selectedYear', newValue, '1d'); // Store in cookies
  localStorage.setItem('selectedYear', newValue); // Survive page reloads
});
